import { useEffect, useState, type FormEvent } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { InlineLoader } from '@/components/ui/InlineLoader';
import { promptsService } from '@/services/prompts.service';
import { ApiError } from '@/services/http';
import { cn } from '@/lib/cn';
import type { PromptView } from '@/types/api';

/**
 * BookPromptManager — the Learn-With-Ollie prompt CRUD card on the admin
 * book editor's left column (CLIENT_PLAN §Phase 7 + ADMIN_DASHBOARD §Prompt
 * Management). Renders a numbered list of prompts with a status line below
 * each one so the admin can see at a glance which questions have been
 * answered by the AI cache and which are still pending.
 *
 * Wiring:
 *   - On mount + after every mutation we call `GET /books/:id/prompts`. We
 *     deliberately re-fetch rather than splicing the local list because
 *     the server may eventually add moderation state we want to surface.
 *   - CRUD is per-row — no batch save. Each row's Edit action toggles to
 *     an inline input; Save submits `PATCH /prompts/:id`. Delete goes
 *     through `ConfirmDialog` so we don't lose a prompt by mis-click.
 *   - New prompts are appended via the bottom "+ Add" form. Empty / all
 *     whitespace input is rejected client-side with a toast.
 *
 * Status copy mirrors the plan exactly:
 *   - cached response → `"AI response generated · N words"`
 *   - no cached response → `"Pending — no response yet"`
 */
export interface BookPromptManagerProps {
  bookId: string;
  className?: string;
}

function wordCount(text: string): number {
  const trimmed = text.trim();
  if (!trimmed) return 0;
  return trimmed.split(/\s+/).length;
}

export function BookPromptManager({ bookId, className }: BookPromptManagerProps) {
  const [prompts, setPrompts] = useState<PromptView[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Which row is currently in edit mode (id → draft text). Only one row is
  // editable at a time — keeping a scalar id avoids branchy state.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingDraft, setEditingDraft] = useState('');
  const [savingId, setSavingId] = useState<string | null>(null);
  // "+ Add" state at the bottom of the card.
  const [newPrompt, setNewPrompt] = useState('');
  const [adding, setAdding] = useState(false);
  // Delete confirmation.
  const [deletingPrompt, setDeletingPrompt] = useState<PromptView | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await promptsService.listForBook(bookId);
        if (cancelled) return;
        // Server returns newest first; keep that ordering so admins see
        // their latest additions at the top of the numbered list.
        setPrompts(rows);
      } catch (err) {
        if (cancelled) return;
        setLoadError(err instanceof ApiError ? err.message : 'Could not load prompts.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bookId]);

  const handleStartEdit = (prompt: PromptView) => {
    setEditingId(prompt.id);
    setEditingDraft(prompt.prompt ?? '');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingDraft('');
  };

  const handleSaveEdit = async (prompt: PromptView) => {
    const next = editingDraft.trim();
    if (!next) {
      toast.error('Prompt text cannot be empty.');
      return;
    }
    if (next === (prompt.prompt ?? '')) {
      // Nothing changed — just exit edit mode silently.
      handleCancelEdit();
      return;
    }
    setSavingId(prompt.id);
    try {
      const updated = await promptsService.update(prompt.id, { prompt: next });
      setPrompts((prev) =>
        prev ? prev.map((row) => (row.id === updated.id ? updated : row)) : prev,
      );
      toast.success('Prompt updated.');
      handleCancelEdit();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not save the prompt.');
    } finally {
      setSavingId(null);
    }
  };

  const handleAdd = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const text = newPrompt.trim();
    if (!text) {
      toast.error('Type a prompt before adding.');
      return;
    }
    setAdding(true);
    try {
      const created = await promptsService.create(bookId, { prompt: text });
      // Server orders newest-first; match that locally instead of re-fetching.
      setPrompts((prev) => (prev ? [created, ...prev] : [created]));
      setNewPrompt('');
      toast.success('Prompt added.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not add the prompt.');
    } finally {
      setAdding(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletingPrompt) return;
    setDeleting(true);
    try {
      await promptsService.remove(deletingPrompt.id);
      setPrompts((prev) => (prev ? prev.filter((row) => row.id !== deletingPrompt.id) : prev));
      toast.success('Prompt deleted.');
      setDeletingPrompt(null);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not delete the prompt.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div
      className={cn(
        'flex flex-col gap-4 rounded-2xl border border-or-slate-200 bg-white p-5 shadow-sm',
        className,
      )}
    >
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-[18px] font-bold text-or-slate-900">
            Ask Ollie prompts
          </h2>
          <p className="mt-1 text-[12px] text-or-slate-500">
            Seed questions young readers can tap to hear Ollie's cached answer.
          </p>
        </div>
      </div>

      {loadError ? (
        <p className="rounded-lg bg-or-rose-100 px-3 py-2 text-[12px] text-or-rose-700">
          {loadError}
        </p>
      ) : null}

      {prompts === null && !loadError ? <InlineLoader label="Loading prompts…" /> : null}

      {prompts !== null ? (
        prompts.length === 0 ? (
          <p className="rounded-xl border border-dashed border-or-slate-200 bg-or-slate-50 px-4 py-6 text-center text-[12px] text-or-slate-500">
            No prompts yet. Add the first one below.
          </p>
        ) : (
          <ol className="flex flex-col gap-3">
            {prompts.map((prompt, index) => {
              const isEditing = editingId === prompt.id;
              const busy = savingId === prompt.id;
              const hasResponse = !!prompt.response && prompt.response.trim().length > 0;
              const count = hasResponse ? wordCount(prompt.response ?? '') : 0;
              return (
                <li
                  key={prompt.id}
                  className="flex gap-3 rounded-xl border border-or-slate-200 bg-or-cream-50/60 p-3"
                >
                  <span
                    aria-hidden
                    className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-or-orange-100 text-[12px] font-semibold text-or-orange-700"
                  >
                    {index + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    {isEditing ? (
                      <div className="flex flex-col gap-2">
                        <textarea
                          autoFocus
                          value={editingDraft}
                          onChange={(e) => setEditingDraft(e.target.value)}
                          rows={3}
                          maxLength={2000}
                          className="w-full rounded-lg border border-or-slate-200 bg-white px-3 py-2 text-[13px] text-or-slate-900 placeholder:text-or-slate-400 focus:border-or-orange-400 focus:outline-none focus:ring-2 focus:ring-or-orange-500/30"
                        />
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            onClick={handleCancelEdit}
                            disabled={busy}
                          >
                            Cancel
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => void handleSaveEdit(prompt)}
                            disabled={busy}
                          >
                            {busy ? 'Saving…' : 'Save'}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <p className="text-[13px] leading-relaxed text-or-slate-800">
                          {prompt.prompt ?? (
                            <span className="italic text-or-slate-400">Empty prompt</span>
                          )}
                        </p>
                        <p
                          className={cn(
                            'mt-1 text-[11px] font-semibold uppercase tracking-wider',
                            hasResponse ? 'text-or-green-700' : 'text-or-slate-400',
                          )}
                        >
                          {hasResponse
                            ? `AI response generated · ${count} word${count === 1 ? '' : 's'}`
                            : 'Pending — no response yet'}
                        </p>
                      </>
                    )}
                  </div>
                  {!isEditing ? (
                    <div className="flex shrink-0 items-start gap-1">
                      <button
                        type="button"
                        onClick={() => handleStartEdit(prompt)}
                        aria-label="Edit prompt"
                        className="rounded-full p-1.5 text-or-slate-500 hover:bg-or-slate-100 hover:text-or-slate-800"
                      >
                        <Pencil className="h-3.5 w-3.5" aria-hidden />
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeletingPrompt(prompt)}
                        aria-label="Delete prompt"
                        className="rounded-full p-1.5 text-or-rose-500 hover:bg-or-rose-100 hover:text-or-rose-700"
                      >
                        <Trash2 className="h-3.5 w-3.5" aria-hidden />
                      </button>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ol>
        )
      ) : null}

      <form onSubmit={handleAdd} className="flex items-center gap-2">
        <input
          type="text"
          value={newPrompt}
          onChange={(e) => setNewPrompt(e.target.value)}
          placeholder="Add a new prompt…"
          maxLength={2000}
          disabled={adding || prompts === null}
          className="h-10 min-w-0 flex-1 rounded-full border border-or-slate-200 bg-white px-4 text-[13px] text-or-slate-900 placeholder:text-or-slate-400 focus:border-or-orange-400 focus:outline-none focus:ring-2 focus:ring-or-orange-500/30 disabled:cursor-not-allowed disabled:opacity-60"
        />
        <Button type="submit" size="md" disabled={adding || prompts === null}>
          <Plus className="h-4 w-4" aria-hidden />
          {adding ? 'Adding…' : 'Add'}
        </Button>
      </form>

      <ConfirmDialog
        open={!!deletingPrompt}
        onOpenChange={(open) => {
          if (!open && !deleting) setDeletingPrompt(null);
        }}
        title="Delete this prompt?"
        description="The prompt and its cached AI response will be permanently removed from this book."
        confirmLabel="Delete prompt"
        isConfirming={deleting}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
}
